import {
    Alert,
    Avatar,
    Box,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Fab,
    FormControl,
    Grid,
    InputAdornment,
    InputLabel,
    OutlinedInput,
    Select,
    MenuItem,
    Snackbar,
    Typography,
} from '@mui/material'
import { Component } from '../../types/ContentStructure'
import { FC, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { getAllCreditsV2, getCreditResultsV2, getAmortizationTableV2 } from '../../services/CreditV2Service'
import { postNewLead } from '../../services/CreditService'
import { MAIN_COLOR, SECONDARY_COLOR } from '../../utils/constants'
import { slugify } from '../../utils/utils'
import CalculateIcon from '@mui/icons-material/Calculate'
import CloseIcon from '@mui/icons-material/Close'
import PhoneInput from '../inputs/PhoneInput'
import CreditResultsModal from '../general/CreditResultsModal'
import ApointmentModal from '../general/ApointmentModal'
import { red } from '@mui/material/colors'

interface IComponentFormProps {
    component: Component
}

export const formatter = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' })

const ComponentForm: FC<IComponentFormProps> = ({ component }) => {
    const { image } = component
    const location = useLocation()

    // ── Créditos ─────────────────────────────────────────────────────────────
    const [credits, setCredits] = useState<any[]>([])
    const [selectedCredit, setSelectedCredit] = useState<any>(null)
    const [lockedCredit, setLockedCredit] = useState(false) // pre-seleccionado por URL

    // ── Calculadora ──────────────────────────────────────────────────────────
    const [amount, setAmount] = useState<number>(0)
    const [boolLimite, setBoolLimite] = useState(false)
    const [selectedPlazo, setSelectedPlazo] = useState<any>(null)
    const [periodicity, setPeriodicity] = useState({ value: 0, name: '', pluralName: '', id: 0 })
    const [calcResult, setCalcResult] = useState<any>(null)
    const [loadingCalc, setLoadingCalc] = useState(false)
    const [boolError, setBoolError] = useState(false)
    const [dateCalc, setDateCalc] = useState('')
    const [dateVigencia, setDateVigencia] = useState('')

    // ── Modales ──────────────────────────────────────────────────────────────
    const [openLead, setOpenLead] = useState(false)
    const [openAmort, setOpenAmort] = useState(false)
    const [openAppoint, setOpenAppoint] = useState(false)
    const [meet, setMeet] = useState<any>({})

    // ── Lead form ────────────────────────────────────────────────────────────
    const [leadName, setLeadName] = useState('')
    const [leadEmail, setLeadEmail] = useState('')
    const [leadPhone, setLeadPhone] = useState('')
    const [leadLoading, setLeadLoading] = useState(false)
    const [leadAlert, setLeadAlert] = useState(false)
    const [leadSuccess, setLeadSuccess] = useState(false)

    // ─────────────────────────────────────────────────────────────────────────
    // Carga inicial de créditos

    useEffect(() => {
        const load = async () => {
            try {
                const res = await getAllCreditsV2()
                if (res.status === 200) {
                    const list: any[] = res.data
                    setCredits(list)

                    // Intentar pre-seleccionar por slug de la URL actual
                    // URL: /{productoTipo}/{productoSlug}  ej: /credito/credito-personal
                    const urlSlug = location.pathname.split('/').filter(Boolean).pop() || ''
                    const match = list.find(
                        (c: any) =>
                            slugify(c.product?.name || '') === urlSlug ||
                            slugify(c.product?.title || '') === urlSlug
                    )

                    const first = match || list[0]
                    if (first) {
                        selectCredit(first)
                        setLockedCredit(false) // pre-seleccionado pero editable
                    }
                }
            } catch (e) {
                console.error('Error cargando créditos v2:', e)
            }
        }
        load()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname])

    // ─────────────────────────────────────────────────────────────────────────
    // Seleccionar crédito y primer plazo

    const selectCredit = (credit: any) => {
        setSelectedCredit(credit)
        setAmount(credit.min)
        setBoolLimite(false)
        setBoolError(false)
        setCalcResult(null)

        const firstPlazo = credit.rangetimecreditList?.slice().sort((a: any, b: any) => a.rangetime.value - b.rangetime.value)[credit.rangetimecreditList.length - 1]
        if (firstPlazo) {
            setSelectedPlazo(firstPlazo)
            setPeriodicity({
                value: firstPlazo.rangetime.value,
                name: firstPlazo.rangetime.periodicity.name,
                pluralName: firstPlazo.rangetime.periodicity.pluralName,
                id: firstPlazo.idrangetime,
            })
            calcular(credit.id, firstPlazo.idrangetime, credit.min)
        }
    }

    // ─────────────────────────────────────────────────────────────────────────
    // Calcular

    const calcular = async (creditId: number, idplazo: number, amt: number) => {
        if (!creditId || !idplazo || amt <= 0) return
        try {
            setLoadingCalc(true)
            const res = await getCreditResultsV2(creditId, idplazo, amt)
            if (res.status === 200) {
                if (parseFloat(res.data.catX) > 30) {
                    res.data.catX = 30.00
                }
                setCalcResult(res.data)
                setBoolError(false)
                const fechaHoy = new Date().toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })
                const fechaVig = new Date(new Date().setMonth(new Date().getMonth() + 6)).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })
                console.log('DEBUG ComponentForm -> Hoy:', fechaHoy, '| Vigencia + 6 meses:', fechaVig)
                setDateCalc(fechaHoy)
                setDateVigencia(fechaVig)
            }
        } catch {
            setBoolError(true)
        } finally {
            setLoadingCalc(false)
        }
    }

    const handleAmountChange = (val: number) => {
        setAmount(val)
        if (!selectedCredit) return
        const fuera = val < selectedCredit.min || val > selectedCredit.max
        setBoolLimite(fuera)
        if (!fuera && selectedPlazo) {
            calcular(selectedCredit.id, selectedPlazo.idrangetime, val)
        }
    }

    const handlePlazoClick = (plazo: any) => {
        setSelectedPlazo(plazo)
        setPeriodicity({
            value: plazo.rangetime.value,
            name: plazo.rangetime.periodicity.name,
            pluralName: plazo.rangetime.periodicity.pluralName,
            id: plazo.idrangetime,
        })
        if (!boolLimite && selectedCredit) {
            calcular(selectedCredit.id, plazo.idrangetime, amount)
        }
    }

    // ─────────────────────────────────────────────────────────────────────────
    // Enviar lead

    const handleSendLead = async () => {
        if (!leadName || !leadPhone) { setLeadAlert(true); return }
        setLeadLoading(true)
        try {
            const response = await postNewLead({
                name: leadName,
                email: leadEmail,
                phone: leadPhone,
                title: selectedCredit?.product?.title,
                idCredit: selectedCredit?.id,
                amount,
                idPlazo: periodicity.id,
                meet: Object.keys(meet).length ? buildMeetString(meet) : '',
                min: selectedCredit?.min,
                max: selectedCredit?.max,
            })
            if (response.status === 200) {
                setLeadSuccess(true)
                setOpenLead(false)
                resetLeadForm()
            }
        } catch {
            setLeadAlert(true)
        } finally {
            setLeadLoading(false)
        }
    }

    const resetLeadForm = () => {
        setLeadName(''); setLeadEmail(''); setLeadPhone('')
    }

    const buildMeetString = (m: any) => {
        try {
            const ts = m.time.toLowerCase().replace('.', '')
            const [hour] = ts.split(':').map((p: any) => parseInt(p, 10))
            const [, indicator] = hour.toString().split(' ')
            let h = hour
            if (indicator === 'p.m.' && h !== 12) h += 12
            if (indicator === 'a.m.' && h === 12) h = 0
            const [month, day, year] = new Date(m.day).toLocaleDateString().split('/').map((p: any) => parseInt(p, 10))
            return `${year}-${day.toString().padStart(2, '0')}-${month.toString().padStart(2, '0')}T${h.toString().padStart(2, '0')}:00:00`
        } catch { return '' }
    }

    // ─────────────────────────────────────────────────────────────────────────
    // Render

    if (!selectedCredit) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
                <CircularProgress color="error" />
            </Box>
        )
    }

    const canCalculate = !boolLimite && amount >= selectedCredit.min && amount <= selectedCredit.max
    const plazos: any[] = (selectedCredit.rangetimecreditList || [])
        .slice().sort((a: any, b: any) => a.rangetime.value - b.rangetime.value)

    return (
        <Box id="Form" sx={{ width: '100%', mb: 10 }}>
            <Container maxWidth="xl"
                sx={{}}>
                {/* ── Contenedor unificado: imagen izq + formulario der ── */}
                <Box sx={{
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'stretch',
                    borderRadius: { xs: '20px', md: '0px 20px 20px 0px' },
                    overflow: 'hidden',
                    boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
                    minHeight: { xs: 'auto', md: 420 },
                    color: '#FFF'
                }}>

                    {/* Imagen izquierda — oculta en mobile */}
                    <Box sx={{
                        display: { xs: 'none', md: 'block' },
                        flex: '0 0 55%',// ocupa 55% fijo
                        position: 'relative',
                        overflow: 'hidden',
                    }}>
                        <Box
                            component="img"
                            src={image || ''}
                            alt=""
                            sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                objectPosition: 'center top',
                                display: 'block',
                            }}
                        />
                    </Box>
                    {/* Formulario derecha — 100% en mobile, 45% en desktop */}
                    <Box id="simulador-credito" sx={{
                        backgroundColor: '#001933',
                        color: '#FFF',
                        width: '100%'
                    }}>
                        {/* ── Título de sección ─────────────────────────────────── */}
                        <Box sx={{ my: 2, textAlign: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 1 }}>
                                <Avatar sx={{ bgcolor: MAIN_COLOR, width: 48, height: 48 }}>
                                    <CalculateIcon />
                                </Avatar>
                                <Typography variant="h3" fontWeight={100} >
                                    Simula tu crédito
                                </Typography>
                            </Box>
                            <Typography variant="body1" >
                                Calcula tu pago mensual de forma rápida y transparente
                            </Typography>
                        </Box>
                        <Container maxWidth="xl"
                            sx={{ px: { xs: 2, sm: 3, md: 6, lg: 0 } }}>
                            <Grid container spacing={1}>
                                <Grid item xs={12} lg={7}>
                                    <Box sx={{
                                        p: { xs: 3, md: 2 },

                                        '& .MuiInputLabel-root': {
                                            color: 'white',
                                        },
                                    }}>

                                        {/* Selector de crédito */}
                                        {lockedCredit ? (
                                            // Vista estática cuando está bloqueado
                                            <Box sx={{ mb: 3 }}>
                                                <Typography sx={{
                                                    color: '#FFFA',
                                                    fontSize: '0.75rem',
                                                    mb: 1,
                                                }}>Tipo de crédito</Typography>
                                                <Box sx={{
                                                    p: 1.5,
                                                    borderBottom: `1px solid ${MAIN_COLOR}`,
                                                    color: '#FFFA',
                                                    opacity: 0.8,
                                                }}>
                                                    <Typography fontWeight={600}>
                                                        {selectedCredit?.product?.title}
                                                    </Typography>
                                                    <Typography variant="caption">
                                                        Tasa {selectedCredit?.rate}% anual · {formatter.format(selectedCredit?.min)} – {formatter.format(selectedCredit?.max)}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        ) : (
                                            // Select normal cuando no está bloqueado
                                            <FormControl fullWidth sx={{
                                                mb: 3,
                                                '& .MuiOutlinedInput-root': {
                                                    color: 'white',
                                                    '& fieldset': {
                                                        borderColor: 'transparent',
                                                        borderBottom: `1px solid ${MAIN_COLOR}`,
                                                        borderRadius: 0,
                                                        borderWidth: '0 0 1px 0',
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: 'transparent',
                                                        borderBottom: `1px solid ${MAIN_COLOR}`,
                                                        borderRadius: 0,
                                                        borderWidth: '0 0 1px 0',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: 'transparent',
                                                        borderBottom: `1px solid ${MAIN_COLOR}`,
                                                        borderRadius: 0,
                                                        borderWidth: '0 0 1px 0',
                                                    },
                                                },
                                            }}>
                                                <InputLabel sx={{
                                                    color: 'white',
                                                    '&.Mui-focused': {
                                                        color: 'white',
                                                    },
                                                }}>Tipo de crédito</InputLabel>
                                                <Select
                                                    label="Tipo de crédito"
                                                    value={selectedCredit?.id || ''}
                                                    onChange={e => {
                                                        const c = credits.find((x: any) => x.id === e.target.value)
                                                        if (c) selectCredit(c)
                                                    }}
                                                    renderValue={(value) => {
                                                        const credit = credits.find((c: any) => c.id === value)
                                                        if (!credit) return ''
                                                        return (
                                                            <Box>
                                                                <Typography fontWeight={600} color="white">
                                                                    {credit.product?.title}
                                                                </Typography>
                                                                <Typography variant="caption" color="white">
                                                                    Tasa {credit.rate}% anual · {formatter.format(credit.min)} – {formatter.format(credit.max)}
                                                                </Typography>
                                                            </Box>
                                                        )
                                                    }}
                                                >
                                                    {credits.map((c: any) => (
                                                        <MenuItem key={c.id} value={c.id}>
                                                            <Box>
                                                                <Typography fontWeight={600} >
                                                                    {c.product?.title}
                                                                </Typography>
                                                                <Typography variant="caption" >
                                                                    Tasa {c.rate}% anual · {formatter.format(c.min)} – {formatter.format(c.max)}
                                                                </Typography>
                                                            </Box>
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        )}

                                        {/* Info del crédito seleccionado */}
                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
                                            <Chip label={`Tasa ${selectedCredit.rate}% anual`} size="small"
                                                sx={{ bgcolor: '#FFFA', color: SECONDARY_COLOR, fontWeight: 600 }} />
                                            <Chip label={selectedCredit.regime?.name || (selectedCredit.idregimen === 1 ? 'P. Física' : 'P. Moral')}
                                                size="small" variant="outlined"
                                                sx={{ bgcolor: '#FFFA', color: SECONDARY_COLOR }}
                                            />
                                            <Chip label={`Mín ${formatter.format(selectedCredit.min)}`} size="small" variant="outlined"
                                                sx={{ bgcolor: '#FFFA', color: SECONDARY_COLOR }} />
                                            <Chip label={`Máx ${formatter.format(selectedCredit.max)}`} size="small" variant="outlined"
                                                sx={{ bgcolor: '#FFFA', color: SECONDARY_COLOR }} />
                                        </Box>

                                        {/* Monto */}
                                        <FormControl
                                            fullWidth
                                            variant="outlined"
                                            error={boolLimite}
                                            sx={{
                                                mb: 3,
                                            }}
                                        >
                                            <InputLabel sx={{
                                                color: 'white',
                                                '&.Mui-focused': {
                                                    color: 'white',
                                                },
                                            }}>Monto a solicitar</InputLabel>
                                            <OutlinedInput
                                                type="number"
                                                value={amount}
                                                label="Monto a solicitar"
                                                startAdornment={<InputAdornment position="start"><span style={{ color: 'white' }}>$</span></InputAdornment>}
                                                onChange={e => handleAmountChange(+e.target.value)}
                                                sx={{
                                                    fontSize: 20, fontWeight: 600,
                                                    borderRadius: '12px',
                                                    color: 'white',
                                                    '& fieldset': {
                                                        borderColor: MAIN_COLOR,
                                                        borderWidth: '1px',
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: MAIN_COLOR,
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: boolLimite ? 'error.main' : MAIN_COLOR,
                                                    },
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: MAIN_COLOR,
                                                    },
                                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: MAIN_COLOR,
                                                    },
                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: boolLimite ? 'error.main' : MAIN_COLOR,
                                                    },
                                                }}
                                            />
                                        </FormControl>

                                        {boolLimite && (
                                            <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                                                Monto válido: <b>{formatter.format(selectedCredit.min)}</b> – <b>{formatter.format(selectedCredit.max)}</b>
                                            </Alert>
                                        )}

                                        {/* Plazos */}
                                        <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                                            Elige tu plazo
                                            <Typography component="span" variant="caption" sx={{ ml: 1 }}>
                                                (en {plazos[0]?.rangetime?.periodicity?.pluralName || 'meses'})
                                            </Typography>
                                        </Typography>

                                        <Grid container spacing={1} sx={{ mb: 2, alignItems: 'center' }}>
                                            {plazos.map((p: any) => {
                                                const active = selectedPlazo?.id === p.id
                                                return (
                                                    <Grid item key={p.id} >
                                                        <Fab
                                                            size="medium"
                                                            onClick={() => handlePlazoClick(p)}
                                                            sx={{
                                                                //width: '100%',
                                                                bgcolor: active ? MAIN_COLOR : 'grey.200',
                                                                color: active ? 'white' : 'text.primary',
                                                                fontWeight: 700,
                                                                fontSize: 15,
                                                                boxShadow: active ? '0 4px 12px rgba(219,29,31,0.3)' : 'none',
                                                                '&:hover': {
                                                                    bgcolor: active ? MAIN_COLOR : 'grey.300',
                                                                    transform: 'scale(1.05)',
                                                                },
                                                                transition: 'all 0.2s ease',
                                                                aspectRatio: '1 / 1',
                                                            }}
                                                        >
                                                            {p.rangetime.value}
                                                        </Fab>
                                                    </Grid>
                                                )
                                            })}
                                        </Grid>

                                    </Box>
                                </Grid>
                                <Grid item xs={12} lg={5}>
                                    <Box sx={{ p: { xs: 3, md: 2 } }}>
                                        <Typography variant="h6" fontWeight={600} textAlign="center" mb={3}>
                                            Tu pago estimado
                                        </Typography>

                                        {loadingCalc ? (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                                <CircularProgress sx={{ color: SECONDARY_COLOR }} />
                                            </Box>
                                        ) : canCalculate && calcResult ? (
                                            <>
                                                <Box sx={{ textAlign: 'center', mb: 3 }}>
                                                    <Typography variant="h2" fontWeight={700} >
                                                        {formatter.format(calcResult.pago?.pago || 0)}
                                                    </Typography>
                                                    <Typography variant="body1">
                                                        por {periodicity.value}{' '}
                                                        {periodicity.value === 1 ? periodicity.name : periodicity.pluralName}
                                                    </Typography>
                                                </Box>

                                                {/* Mini resumen */}
                                                <Box sx={{ bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2, p: 2, mb: 3 }}>
                                                    {[
                                                        { label: 'Tasa anual', val: `${selectedCredit.rate}%` },
                                                        { label: 'CAT promedio', val: `${parseFloat(calcResult.catX || '0').toFixed(1) || '---'}% Sin IVA` },
                                                        { label: 'Crédito', val: formatter.format(amount) },
                                                        { label: 'Plazo', val: `${periodicity.value} ${periodicity.pluralName}` },
                                                    ].map(row => (
                                                        <Box key={row.label} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                                                            <Typography variant="body2" >{row.label}</Typography>
                                                            <Typography variant="body2" fontWeight={600}>{row.val}</Typography>
                                                        </Box>
                                                    ))}
                                                </Box>

                                                {/* CTAs */}
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                                    <Fab
                                                        variant="extended"
                                                        sx={{
                                                            bgcolor: '#FFFE',
                                                            color: SECONDARY_COLOR,
                                                            fontWeight: 700, fontSize: 16, textTransform: 'none',
                                                            borderRadius: 2, py: 1.5,
                                                            '&:hover': { bgcolor: MAIN_COLOR, color: 'white', transform: 'translateY(-2px)' },
                                                            transition: 'all 0.25s ease',
                                                        }}
                                                        onClick={() => setOpenLead(true)}
                                                    >
                                                        ¡Solicítalo ahora!
                                                    </Fab>

                                                    {/* <Box sx={{ display: 'flex', gap: 1 }}>
                                            <Fab
                                                variant="extended"
                                                size="small"
                                                sx={{
                                                    flex: 1, bgcolor: 'transparent',
                                                    border: `1px solid ${SECONDARY_COLOR}`,
                                                    color: SECONDARY_COLOR, fontWeight: 600,
                                                    textTransform: 'none', fontSize: 13,
                                                    '&:hover': { bgcolor: 'rgba(219,29,31,0.05)' },
                                                }}
                                                onClick={() => setOpenAmort(true)}
                                            >
                                                Ver tabla completa
                                            </Fab>
                                            <Fab
                                                variant="extended"
                                                size="small"
                                                sx={{
                                                    flex: 1, bgcolor: 'transparent',
                                                    border: `1px solid ${SECONDARY_COLOR}`,
                                                    color: SECONDARY_COLOR, fontWeight: 600,
                                                    textTransform: 'none', fontSize: 13,
                                                    '&:hover': { bgcolor: 'rgba(219,29,31,0.05)' },
                                                }}
                                                onClick={() => setOpenAppoint(true)}
                                            >
                                                Agendar cita
                                            </Fab>
                                        </Box> */}
                                                </Box>
                                            </>
                                        ) : (
                                            <Alert severity="info" sx={{ borderRadius: 2 }}>
                                                Ingresa un monto válido para ver tu estimado
                                            </Alert>
                                        )}
                                    </Box>
                                </Grid>
                            </Grid>

                        </Container>

                        {/* ── Modal: Solicitar crédito ──────────────────────────────── */}
                        <Dialog open={openLead} onClose={() => setOpenLead(false)} fullWidth maxWidth="sm"
                            PaperProps={{ sx: { borderRadius: 3, bgcolor: SECONDARY_COLOR } }}>
                            <DialogTitle sx={{ color: 'white', pb: 1 }}>
                                <Fab size="small" onClick={() => setOpenLead(false)}
                                    sx={{ position: 'absolute', right: 16, top: 16, bgcolor: MAIN_COLOR, color: 'white' }}>
                                    <CloseIcon fontSize="small" />
                                </Fab>
                                <Typography variant="h5" fontWeight={700} color="white">Solicita tu crédito</Typography>
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', mt: 0.5 }}>
                                    {selectedCredit.product?.title} · {formatter.format(amount)} · {periodicity.value} {periodicity.pluralName}
                                </Typography>
                            </DialogTitle>
                            <DialogContent sx={{ pt: 2 }}>
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 2,
                                    pt: 2,
                                    '& .MuiOutlinedInput-root': {
                                        color: 'white',
                                        '& fieldset': {
                                            borderColor: 'transparent',
                                            borderBottom: `1px solid ${MAIN_COLOR}`,
                                            borderRadius: 0,
                                            borderWidth: '0 0 1px 0',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: 'transparent',
                                            borderBottom: `1px solid ${MAIN_COLOR}`,
                                            borderRadius: 0,
                                            borderWidth: '0 0 1px 0',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: 'transparent',
                                            borderBottom: `1px solid ${MAIN_COLOR}`,
                                            borderRadius: 0,
                                            borderWidth: '0 0 1px 0',
                                        },
                                    },
                                }}>
                                    <FormControl fullWidth sx={{ mb: 2 }}>
                                        <InputLabel sx={{
                                            color: 'white',
                                            '&.Mui-focused': {
                                                color: 'white',
                                            },
                                        }}>Nombre completo *</InputLabel>
                                        <OutlinedInput
                                            value={leadName}
                                            onChange={e => setLeadName(e.target.value)}
                                            sx={{
                                                '& fieldset': {
                                                    borderColor: 'transparent',
                                                    borderBottom: `1px solid ${MAIN_COLOR}`,
                                                    borderRadius: 0,
                                                    borderWidth: '0 0 1px 0',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: 'transparent',
                                                    borderBottom: `1px solid ${MAIN_COLOR}`,
                                                    borderRadius: 0,
                                                    borderWidth: '0 0 1px 0',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: 'transparent',
                                                    borderBottom: `1px solid ${MAIN_COLOR}`,
                                                    borderRadius: 0,
                                                    borderWidth: '0 0 1px 0',
                                                },
                                            }}
                                        />
                                    </FormControl>
                                    <FormControl fullWidth sx={{ mb: 2 }}>
                                        <InputLabel sx={{
                                            color: 'white',
                                            '&.Mui-focused': {
                                                color: 'white',
                                            },
                                        }}>Correo electrónico</InputLabel>
                                        <OutlinedInput
                                            type="email"
                                            value={leadEmail}
                                            onChange={e => setLeadEmail(e.target.value)}
                                            sx={{
                                                '& fieldset': {
                                                    borderColor: 'transparent',
                                                    borderBottom: `1px solid ${MAIN_COLOR}`,
                                                    borderRadius: 0,
                                                    borderWidth: '0 0 1px 0',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: 'transparent',
                                                    borderBottom: `1px solid ${MAIN_COLOR}`,
                                                    borderRadius: 0,
                                                    borderWidth: '0 0 1px 0',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: 'transparent',
                                                    borderBottom: `1px solid ${MAIN_COLOR}`,
                                                    borderRadius: 0,
                                                    borderWidth: '0 0 1px 0',
                                                },
                                            }}
                                        />
                                    </FormControl>
                                    <PhoneInput value={leadPhone} onChange={setLeadPhone} label="Número de teléfono *"
                                        sx={{
                                            color: 'white',
                                            '& .MuiFormLabel-root': {
                                                color: 'white',
                                                '& .Mui-focused': {
                                                    color: 'white',
                                                },
                                            },
                                            '& .MuiOutlinedInput-root': {
                                                color: 'white',
                                                '& fieldset': {
                                                    borderColor: 'transparent',
                                                    borderBottom: `1px solid ${MAIN_COLOR}`,
                                                    borderRadius: 0,
                                                    borderWidth: '0 0 1px 0',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: 'transparent',
                                                    borderBottom: `1px solid ${MAIN_COLOR}`,
                                                    borderRadius: 0,
                                                    borderWidth: '0 0 1px 0',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: 'transparent',
                                                    borderBottom: `1px solid ${MAIN_COLOR}`,
                                                    borderRadius: 0,
                                                    borderWidth: '0 0 1px 0',
                                                },
                                            },
                                        }} />

                                    {/* Resumen de lo que va a solicitar */}
                                    <Box sx={{ bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2, p: 2 }}>
                                        {[
                                            { l: 'Producto', v: selectedCredit.product?.title },
                                            { l: 'Monto', v: formatter.format(amount) },
                                            { l: 'Plazo', v: `${periodicity.value} ${periodicity.pluralName}` },
                                            { l: 'Pago est.', v: formatter.format(calcResult?.pago?.pago || 0) },
                                        ].map(r => (
                                            <Box key={r.l} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.3 }}>
                                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>{r.l}</Typography>
                                                <Typography variant="caption" fontWeight={700} color="white">{r.v}</Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            </DialogContent>
                            <DialogActions
                                onClick={handleSendLead}
                                sx={{
                                    bgcolor: MAIN_COLOR, cursor: 'pointer', justifyContent: 'center', py: 2,
                                    '&:hover': { bgcolor: '#c01518' },
                                }}
                            >
                                {leadLoading
                                    ? <CircularProgress size={24} sx={{ color: 'white' }} />
                                    : <Typography variant="h6" fontWeight={700} color="white">Enviar solicitud</Typography>
                                }
                            </DialogActions>
                        </Dialog>

                        {/* ── Modal: Tabla de amortización ──────────────────────────── */}
                        <CreditResultsModal
                            credit={calcResult}
                            product={selectedCredit}
                            periodicity={periodicity}
                            open={openAmort}
                            close={() => setOpenAmort(false)}
                        />

                        {/* ── Modal: Agendar cita ───────────────────────────────────── */}
                        <ApointmentModal
                            open={openAppoint}
                            close={() => setOpenAppoint(false)}
                            setMeeting={(m: any) => { setMeet(m); setOpenAppoint(false); setOpenLead(true) }}
                            closeWithResponse={() => { }}
                        />

                        {/* ── Snackbars ─────────────────────────────────────────────── */}
                        <Snackbar open={leadSuccess} autoHideDuration={5000} onClose={() => setLeadSuccess(false)}>
                            <Alert severity="success" variant="filled">
                                ¡Solicitud enviada! Nos contactaremos contigo pronto.
                            </Alert>
                        </Snackbar>
                        <Snackbar open={leadAlert} autoHideDuration={4000} onClose={() => setLeadAlert(false)}>
                            <Alert severity="warning" variant="filled">
                                Por favor completa los campos requeridos.
                            </Alert>
                        </Snackbar>
                    </Box>
                </Box>

                {/* Disclaimer */}
                {
                    calcResult && (
                        <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(0,0,0,0.03)', borderRadius: 2 }}>
                            <Typography variant="caption" color="text.secondary" display="block" textAlign="justify" lineHeight={1.6} fontSize={14}>
                                Límite sujeto a políticas de crédito y autorización del Comité de Crédito. Condiciones sujetas a políticas de crédito vigente y de acuerdo a las condiciones específicas del producto: "{selectedCredit.product?.title}" <b style={{ fontSize: 24 }}>CAT PROMEDIO {parseFloat(calcResult.catX || '0').toFixed(1)}% "Sin IVA"</b> para fines informativos y de comparación, calculado al {dateCalc}. Vigencia al {dateVigencia}. CAT (Costo Anual Total). IVA (Impuesto al Valor Agregado). Informes sobre los requisitos. <a href="https://avantthya.com.mx/terminos-condiciones/" target="_blank" rel="noopener noreferrer">Términos y Condiciones</a> de Contratación <a href="https://avantthya.com.mx/" target="_blank" rel="noopener noreferrer">www.avantthya.com.mx</a> Fecha de cálculo {dateCalc}, tasa fija {selectedCredit.rate}% anual sujeto a aprobación. Para un crédito de ${amount} pesos a un plazo de {periodicity.value} {periodicity.pluralName} y tasa fija del {selectedCredit.rate}% anual. Vigencia al {dateVigencia}. Cobertura Nacional. Sujeto a aprobación de crédito. Los datos que se muestran no corresponden a una cotización oficial, es únicamente una referencia de simulación de crédito. Sujeto a cambios sin previo aviso.
                            </Typography>
                        </Box>
                    )
                }
            </Container >
        </Box >
    )
}

export default ComponentForm
